import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { TableSearchComponent } from '../../../shared/table-search/table-search.component';
import { MatTabsModule } from '@angular/material/tabs';
import { RowData } from '../../../shared/interfaces/table.data';
import { MatTableDataSource } from '@angular/material/table';
import { Manageduser } from '../../../interfaces/manageduser';
import { Pendingapprovals } from '../../../interfaces/pendingapprovals';

import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { MatDialog } from '@angular/material/dialog';
import { InviteUserDialogComponent } from '../invite-user-dialog/invite-user-dialog.component';
import { UserService } from '../../../services/user/user.service';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [TableSearchComponent, MatTabsModule, RouterOutlet, ButtonsComponent, CommonModule],
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.css',
})
export class UserManagementComponent implements OnInit {
  dataSource!: MatTableDataSource<RowData<Manageduser | Pendingapprovals>>;
  currentTabIndex = 0; // Default to the first tab
  managedUser = $localize`Manage User`;
  pendingApprovals = $localize`Pending Approvals`;
  groupManagement = $localize`Group Management`;

  userRole: String | null = null;

  btnName: string = $localize`Invite User`;
  btnMatFlat = 'mat-flat-button';
  btnStyleFilledSmall = 'btn-filled-small';
  private dialog = inject(MatDialog);

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private userService: UserService
  ) {
    this.userService.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.userRole = user?.role ?? null;
    });
  }

  ngOnInit(): void {
    if (this.userRole === 'Manager' && !this.router.url.includes('group-management')) {
      this.router.navigate(['/user-management/group-management']);
      return; // Stop further execution until the redirect completes.
    }

    this.dataSource = new MatTableDataSource<RowData<Manageduser | Pendingapprovals>>();
    // Sync the tab index with the current route on component initialization
    this.updateTabIndex();

    // Also handle navigation changes to update the tab index dynamically
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateTabIndex();
      }
    });
  }

  updateTabIndex(): void {
    const currentPath = this.activatedRoute.snapshot.firstChild?.routeConfig?.path;
    if (this.userRole === 'Manager') {
      // For a manager, the "Group Management" tab is always the first and only one visible.
      this.currentTabIndex = 0;
    } else {
      // For an admin, the original logic applies.
      this.currentTabIndex = currentPath === 'pending-approvals' ? 1 : currentPath === 'group-management' ? 2 : 0;
    }
    // Force change detection for UI to reflect updated tab index
    this.cdr.detectChanges();
  }

  onTabChange(index: number): void {
    this.currentTabIndex = index;
    if (this.userRole === 'Manager') {
      // A manager only has one tab, which is group management.
      this.router.navigate(['/user-management/group-management']);
    } else {
      if (index === 0) {
        this.router.navigate(['/user-management']);
      } else if (index === 1) {
        this.router.navigate(['/user-management/pending-approvals']);
      } else if (index === 2) {
        this.router.navigate(['/user-management/group-management']);
      }
    }
  }

  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onChildActivate(childComponent: any): void {
    if (childComponent.dataSourceReady) {
      childComponent.dataSourceReady.subscribe((dataSource: MatTableDataSource<RowData<any, any>>) => {
        this.dataSource = dataSource;
      });
    }
  }

  openInviteDialog(): void {
    const dialogRef = this.dialog.open(InviteUserDialogComponent, {
      panelClass: 'invite-dialog-container',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        //might be useful in future to perform some action
      }
    });
  }
}
